import { AxiosInstance, AxiosResponse } from "axios"
import type { DatamapCheckResponse } from "types/datamap.types"
import { sequenceErrorOf } from "features/DataImport/utils/serverMessage"
import { decodeMapBytes } from "features/DataImport/components/DatamapEditor/utils/readMapFile"

/**
 * Reads the project's stored datamap ($map.csv). The bytes are decoded as an uploaded file is:
 * UTF-8 when valid, otherwise Windows-1252, so a map stored as Excel's plain "CSV" is not mangled.
 *
 * @throws Error with the server's message when the project has no datamap.
 */
export async function getDatamapText(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string
): Promise<{ text: string; encoding: "utf-8" | "windows-1252" }> {
  const response = await axiosInstance.get<ArrayBuffer>(
    `/datafile-get?id=${id}&hint_owner=${owner}&subtype=map&encode=text`,
    { responseType: "arraybuffer" }
  )
  const decoded = decodeMapBytes(new Uint8Array(response.data))
  const error = sequenceErrorOf(decoded.text)
  if (error) throw new Error(error)
  return decoded
}

/** Replaces the project's datamap with `csv` (UTF-8 text). */
export async function saveDatamapText(axiosInstance: AxiosInstance, id: string, owner: string, csv: string): Promise<void> {
  const response = await axiosInstance.post(
    `/datafile-save?id=${id}&hint_owner=${owner}&subtype=map&encode=text&overwrite`,
    csv,
    { headers: { "Content-Type": "text/plain; charset=utf-8" } }
  )
  const error = sequenceErrorOf(response.data)
  if (error) throw new Error(error)
}

/** Checks a datamap against the project's data file; without `csv` the stored datamap is checked. */
export async function checkDatamap(
  axiosInstance: AxiosInstance,
  id: string,
  owner: string,
  csv?: string
): Promise<DatamapCheckResponse> {
  const response = await axiosInstance.post<DatamapCheckResponse>(
    `/datamap-check?id=${id}&hint_owner=${owner}`,
    csv ?? "",
    { headers: { "Content-Type": "text/plain; charset=utf-8" } }
  )
  return response.data
}

/** Combines the data file and datamap again, replacing the project's DQD. */
export async function reimportData(axiosInstance: AxiosInstance, id: string, owner: string): Promise<AxiosResponse<unknown>> {
  return axiosInstance.get(`/datafile-combine?id=${id}&hint_owner=${owner}&overwrite`)
}
